import { useCluster } from '@cast/shared-cluster-context-data-access';

import { useReportCapabilitiesQuery } from 'hooks/queries/cost';
import { useCastwareAgentStatus } from 'hooks/useCastwareAgentStatus';

export type UseNetworkCostPrerequisitesArgs = {
  startTime: string;
  endTime: string;
};

export type UseNetworkCostPrerequisitesResult = {
  clusterId: string;
  hasNetworkAgent: boolean;
  isNetworkDataAvailable: boolean;
  isCapabilitiesLoading: boolean;
  isDataProcessing: boolean;
  capabilitiesError: unknown;
  refetchCapabilities: () => void;
};

export const useNetworkCostPrerequisites = (
  useNetworkCostPrerequisitesArgs: UseNetworkCostPrerequisitesArgs
): UseNetworkCostPrerequisitesResult => {
  const { startTime, endTime } = useNetworkCostPrerequisitesArgs;

  const { cluster } = useCluster();

  const {
    data: capabilities,
    isLoading: isCapabilitiesQueryLoading,
    error: capabilitiesError,
    refetch: refetchCapabilities,
  } = useReportCapabilitiesQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    enabled: !!cluster.id,
  });

  const { isLoading: isEgressdLoading, isInstalled: isEgressdInstalled } =
    useCastwareAgentStatus({ name: 'egressd-exporter' });

  const { isLoading: isKvisorLoading, isInstalled: isKvisorInstalled } =
    useCastwareAgentStatus({ name: 'kvisor' });

  const hasNetworkAgent = isEgressdInstalled || isKvisorInstalled;
  const isCapabilitiesLoading =
    isCapabilitiesQueryLoading || isEgressdLoading || isKvisorLoading;

  return {
    clusterId: cluster.id,
    hasNetworkAgent,
    isNetworkDataAvailable: Boolean(capabilities?.networkDataAvailable),
    isCapabilitiesLoading,
    isDataProcessing: Boolean(
      capabilities?.networkDataReceived && !capabilities?.networkDataAvailable
    ),
    capabilitiesError,
    refetchCapabilities,
  };
};
